import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { Button, Checkbox } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FavAvatar, HelloMuiButton } from 'components';
import { useToast } from 'components/Providers/ToastProvider';
import _ from 'lodash';
import { useModals } from 'pages/MainProvider';
import { useMemo, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { queryClient } from 'routes';
import useCompetitorsStore from 'store/useCompetitorsStore';
import useUserStore from 'store/useUserStore';
import styled from 'styled-components';
import { Agent } from 'types/Agent';
import { MAX_LIMIT } from 'util/Constants';
import { GetCountryCode } from 'util/Funcs';
import { PlanTypes } from 'util/Types';
import { OneLetterAvatar } from '../LetterAvatar';
import LoadingAnimation from '../Loading/LoadingAnimation';
import CompetitorPagination from '../Pagination/CompetitorPagination';
import AddedByYouLabel from './AddedByYou';
import styles from './CompetitorTable.module.css';

const ContentContainter = styled.div`
    height: calc(100vh - 320px);
    @media (max-width: 1280px) {
        height: calc(100vh - 390px);
    }
`;

// approximate fixed row height (px)
const ROW_HEIGHT = 60;

interface CompetitorTableBodyProps {
    competitors: Agent[];
    selectedCompetitors: string[];
    onToggle: (c: Agent, checked: boolean) => void;
    showDetails: (c: Agent) => void;
}

const CompetitorTableBody: React.FC<CompetitorTableBodyProps> = ({ competitors, selectedCompetitors, onToggle, showDetails }) => {
    const Row = ({ index, style }: ListChildComponentProps) => {
        const competitor = competitors[index];
        const checked = selectedCompetitors.includes(competitor.companyId);
        const labelId = `row-${index}`;

        return (
            <div style={style} className={`grid grid-cols-[40px_1fr_1fr_1fr_1fr_1fr_1fr] gap-[5px] ${styles['tr']}`} key={competitor.companyId} id={labelId}>
                <div className="my-auto flex justify-center">
                    <div data-cy="select-competitor">
                        <Checkbox
                            checked={checked}
                            color="primary"
                            onChange={(e) => onToggle(competitor, e.target.checked)}
                            inputProps={{ 'aria-labelledby': labelId }}
                            size="small"
                        />
                    </div>
                </div>
                <div className="col-span-2 flex items-center gap-[10px]">
                    {competitor.websiteUrl ? (
                        <FavAvatar url={competitor.websiteUrl} imageSize={16} containerSize={28} />
                    ) : (
                        <OneLetterAvatar title={competitor.name} />
                    )}
                    <div>
                        <div className={`${checked ? 'text-my-green' : ''} font-medium truncate`}>{competitor.name}</div>
                        {competitor.followers > 0 && <div className="text-[10px]">{`${competitor.followers} followers`}</div>}
                    </div>
                </div>
                <div className="flex gap-[5px] flex-wrap items-center my-auto">
                    {competitor.country && (
                        <ReactCountryFlag svg countryCode={GetCountryCode(competitor.country || '') || ''} style={{ width: '2em', height: '2em' }} />
                    )}
                    <span>{competitor.country}</span>
                </div>
                <div className="truncate my-auto">{competitor.city}</div>
                <div className="truncate my-auto">{competitor.propertyCount || 0}</div>
                <div className="flex justify-end items-center gap-[4px] flex-wrap">
                    {competitor.addedById === useUserStore.getState().userInfo?.userId && <AddedByYouLabel />}
                    <HelloMuiButton className="!h-[24px]" onClick={() => showDetails(competitor)}>
                        View Profile
                    </HelloMuiButton>
                </div>
            </div>
        );
    };

    return (
        <ContentContainter className={`border-x border-b border-my-gray`}>
            {' '}
            {/* remove overflow-auto */}
            <AutoSizer>
                {({ height, width }: { height: number; width: number }) => (
                    <List height={height} itemCount={competitors.length} itemSize={ROW_HEIGHT} width={width}>
                        {Row}
                    </List>
                )}
            </AutoSizer>
        </ContentContainter>
    );
};

const CompetitorTable = () => {
    const { showPreviewCompetitorModal } = useModals();
    const { userInfo, getUserPlan, isPremiumUser } = useUserStore();

    const isOnboardingUser = !userInfo?.subscriptionId;

    const {
        fetchAllCompetitors,
        competitors,
        totalPages,
        currentPage,
        setPage,
        filter,
        selectedCompetitors,
        setSelectedCompetitors,
        addCompetitors,
        addAllCompetitors,
    } = useCompetitorsStore();

    const [competitorId, setCompetitorId] = useState<string>('');

    const { showMessage } = useToast();

    const plan = getUserPlan();

    const showCompetitorDetailsModal = (c: Agent) => {
        showPreviewCompetitorModal(c);
    };

    const { isLoading, isError, error, isRefetching } = useQuery({
        queryKey: ['fetchAllCompetitors', currentPage, filter],
        queryFn: fetchAllCompetitors,
        retry: 2,
        retryDelay: 1000,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5,
    });

    const invalidateQueries = () => {
        const queriesToInvalidate = [
            { queryKey: ['fetchPropertiesStatistics'], exact: true },
            { queryKey: ['fetchCompetitorAllProperties'] },
            { queryKey: ['fetchSavedCompetitors'] },
            { queryKey: ['fetchRegions'] },
        ];

        queriesToInvalidate.forEach((query) => queryClient.resetQueries(query));
    };

    const { isPending, mutate } = useMutation({
        mutationKey: ['addCompetitors'],
        mutationFn: addCompetitors,
        onError: (error) => showMessage('Error', error.message),
        onSuccess: () => {
            invalidateQueries();
            showMessage('Success', 'Collaboration partners saved successfully');
        },
    });

    const { isPending: isSaveAllPending, mutate: mutateSaveAll } = useMutation({
        mutationKey: ['addAllCompetitors'],
        mutationFn: addAllCompetitors,
        onError: (error) => showMessage('Error', error.message),
        onSuccess: () => {
            invalidateQueries();
            showMessage('Success', 'All collaboration partners saved successfully');
        },
    });

    const saveButtonDisabled = useMemo(() => {
        const userSavedCompetitors = userInfo?.savedCompetitors || [];
        const hasChanges = !_.isEqual(_.sortBy(userSavedCompetitors), _.sortBy(selectedCompetitors));
        return !hasChanges || selectedCompetitors.length === 0 || plan.maxSwapCount - userInfo?.swapsThisMonth! === 0;
    }, [userInfo?.savedCompetitors, selectedCompetitors, plan.maxSwapCount, userInfo?.swapsThisMonth]);

    const onCompetitorClicked = (competitor: Agent, isChecked: boolean) => {
        const updated = isChecked ? [...selectedCompetitors, competitor.companyId] : selectedCompetitors.filter((id) => id !== competitor.companyId);
        setSelectedCompetitors(updated);
    };

    const onSelectAllButtonClicked = () => mutateSaveAll();

    function handleSaveSelectedCompetitors() {
        mutate(selectedCompetitors);
    }

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            // Select all competitors that are not already selected
            const newSelected = competitors
                .filter((competitor) => !selectedCompetitors.includes(competitor.companyId))
                .map((competitor) => competitor.companyId);

            const updatedSelection = [...selectedCompetitors, ...newSelected];
            setSelectedCompetitors(updatedSelection);
        } else {
            // Deselect all
            setSelectedCompetitors([]);
        }
    };

    const isAllSelected = competitors.length > 0 && competitors.every((competitor) => selectedCompetitors.includes(competitor.companyId));

    const isSomeSelected = competitors.some((competitor) => selectedCompetitors.includes(competitor.companyId));

    if (isError) showMessage('Error', error.message);

    return (
        <>
            <div className={`relative ${isLoading || isRefetching ? 'opacity-40' : ''}`}>
                {(isLoading || isRefetching) && <LoadingAnimation />}

                <div>
                    {/* Desktop */}
                    <div className="md:block hidden text-[12px] w-full font-medium">
                        <div
                            className={`grid grid-cols-[40px_1fr_1fr_1fr_1fr_1fr_1fr] gap-[5px] rounded-t-[10px] border border-b-0 border-my-gray overflow-hidden bg-[#D9D9D9] bg-opacity-20 font-semibold h-[50px] ${styles['tr']}`}
                        >
                            <div className="my-auto flex justify-center">
                                <Checkbox
                                    checked={isAllSelected}
                                    indeterminate={isSomeSelected && !isAllSelected}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    color="primary"
                                    size="small"
                                />
                            </div>
                            <div className="col-span-2 my-auto">Name</div>
                            <div className="my-auto">COUNTRY</div>
                            <div className="my-auto">CITY</div>
                            <div className="my-auto truncate">LISTINGS</div>
                            <div></div>
                        </div>

                        <CompetitorTableBody
                            competitors={competitors}
                            selectedCompetitors={selectedCompetitors}
                            onToggle={onCompetitorClicked}
                            showDetails={showCompetitorDetailsModal}
                        />
                    </div>

                    {/* Mobile unchanged or adapt similarly */}
                    <div className="md:hidden rounded-[10px] overflow-hidden">
                        {/* mobile code omitted for brevity, keep existing mobile rendering */}
                        {/* ... */}
                    </div>
                </div>

                <div className={`flex md:flex-row flex-col gap-[10px] ${isOnboardingUser ? 'justify-center' : 'justify-between'} pt-[10px]`}>
                    <div className="md:m-0 mx-auto flex items-center gap-[5px] flex-col md:flex-row">
                        <CompetitorPagination totalPages={totalPages} currentPage={currentPage} onPageChange={setPage} />
                    </div>

                    <div className="flex md:flex-row flex-col gap-[5px]">
                        <Button
                            onClick={onSelectAllButtonClicked}
                            className="!rounded-[8px] !h-[42px] !bg-my-blue !text-white !px-[20px]"
                            disabled={isSaveAllPending}
                        >
                            {isSaveAllPending ? 'Saving...' : 'Add and Save All'}
                        </Button>

                        {!isOnboardingUser && (
                            <Button
                                onClick={handleSaveSelectedCompetitors}
                                className={`!rounded-[8px] !h-[42px] ${saveButtonDisabled || isPending ? '!bg-my-gray' : '!bg-my-blue'}`}
                                disabled={saveButtonDisabled || isPending}
                            >
                                <span className={`px-[20px] text-[12px] ${saveButtonDisabled ? '!text-my-purple' : '!text-white'}`}>
                                    {isPending ? 'Saving...' : 'Save Selection'}
                                </span>
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default CompetitorTable;
